import { useState, useEffect } from "react";
import { message } from "antd";
import { cashbackReportService } from "../../../shared/services";

export interface CashbackReportFilters {
  brand_id?: number;
  category_id?: number;
  product_status?: string;
  date_from?: string;
  date_to?: string;
  product_code?: string;
}

export interface CashbackReportData {
  product_id: number;
  product_name: string;
  product_code: string;
  cashback_status: string;
  cashback_specific_percentage: number;
  price: number;
  price_with_tax: number;
  product_status: string;
  brand_id: number;
  brand_name: string;
  category_id: number;
  category_name: string;
  order_id: number;
  cashback_amount: number;
  total_price: number;
  order_date: string;
  order_status_id: number;
  order_status_name: string;
  product_quantity: number;
  total_product_value: number;
  total_product_value_with_tax: number;
  cashback_percentage: number;
  calculated_cashback_amount: number;
  user_id: number;
  user_email: string;
  first_name: string;
  last_name: string;
}

export interface CashbackReportSummary {
  total_cashback_amount: number;
  total_orders: number;
  total_products: number;
  total_users: number;
  by_brand: Record<
    string,
    {
      total_cashback: number;
      total_orders: number;
      total_products: number;
    }
  >;
  by_category: Record<
    string,
    {
      total_cashback: number;
      total_orders: number;
      total_products: number;
    }
  >;
  by_product_status: Record<
    string,
    {
      total_cashback: number;
      total_orders: number;
      total_products: number;
    }
  >;
}

export interface CashbackReportResponse {
  data: CashbackReportData[];
  summary: CashbackReportSummary;
  pagination: {
    total: number;
    limit: number;
    skip: number;
  };
}

export const useCashbackReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<CashbackReportResponse | null>(
    null
  );
  const [filters, setFilters] = useState<CashbackReportFilters>({});

  const fetchReport = async (newFilters?: CashbackReportFilters) => {
    setLoading(true);
    try {
      const queryParams = newFilters || filters;
      const response = await cashbackReportService.find({ query: queryParams });
      setReportData(response);
    } catch (error: any) {
      message.error("Error al cargar el reporte: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateFilters = (newFilters: Partial<CashbackReportFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    fetchReport(updatedFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    fetchReport(clearedFilters);
  };

  const exportToExcel = async () => {
    if (!reportData?.data) {
      message.warning("No hay datos para exportar");
      return;
    }

    try {
      const headers = [
        "ID Producto",
        "Nombre Producto",
        "Código Producto",
        "Estado Cashback",
        "Porcentaje Cashback",
        "Precio",
        "Precio con IVA",
        "Marca",
        "Categoría",
        "ID Orden",
        "Monto Cashback",
        "Precio Total",
        "Fecha Orden",
        "Estado Orden",
        "Cantidad",
        "Valor Total Producto",
        "Valor Total Producto con IVA",
        "Porcentaje Cashback Calculado",
        "Monto Cashback Calculado",
        "Usuario",
        "Email Usuario",
        "Nombre Usuario",
        "Apellido Usuario",
      ];

      const data = reportData.data.map((item) => [
        item.product_id,
        item.product_name,
        item.product_code,
        item.cashback_status,
        item.cashback_specific_percentage,
        item.price,
        item.price_with_tax,
        item.brand_name,
        item.category_name,
        item.order_id,
        item.cashback_amount,
        item.total_price,
        item.order_date,
        item.order_status_name,
        item.product_quantity,
        item.total_product_value,
        item.total_product_value_with_tax,
        item.cashback_percentage,
        item.calculated_cashback_amount,
        item.user_id,
        item.user_email,
        item.first_name,
        item.last_name,
      ]);

      const csvContent = [
        headers.join(","),
        ...data.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `reporte-cashback-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Reporte exportado exitosamente");
    } catch (error: any) {
      message.error("Error al exportar: " + error.message);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  return {
    loading,
    reportData,
    filters,
    fetchReport,
    updateFilters,
    clearFilters,
    exportToExcel,
  };
};
